import React, { useEffect, useState } from 'react';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { FEEDBACK_COMMENT_MAX_LENGTH, FEEDBACK_RATINGS } from '../../../../constants/aiAnalytics';

import { commentLinkLabel, FEEDBACK_ACTION_LABELS, normalizeFeedback, toggledRating } from './MessageFeedback.service';

const QUIET_OPACITY = 0.4;

export default function MessageFeedback({ feedback, onChange }) {
  const persisted = normalizeFeedback(feedback);
  const [rating, setRating] = useState(persisted.rating);
  const [comment, setComment] = useState(persisted.comment);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');

  useEffect(
    function syncPersistedFeedback() {
      setRating(persisted.rating);
      setComment(persisted.comment);
    },
    [persisted.rating, persisted.comment]
  );

  async function applyFeedback(nextRating, nextComment) {
    const previous = { rating, comment };
    setRating(nextRating);
    setComment(nextComment);
    const saved = await onChange(nextRating, nextComment);
    if (!saved) {
      setRating(previous.rating);
      setComment(previous.comment);
    }
  }

  function handleRate(clicked) {
    const nextRating = toggledRating(rating, clicked);
    setCommentOpen(false);
    applyFeedback(nextRating, nextRating ? comment : '');
  }

  function openComment() {
    setCommentDraft(comment);
    setCommentOpen(true);
  }

  function dismissComment() {
    setCommentOpen(false);
    setCommentDraft('');
  }

  function submitComment() {
    setCommentOpen(false);
    applyFeedback(rating, commentDraft.trim());
  }

  function ratingButton(value, ActiveIcon, IdleIcon) {
    const active = rating === value;
    return (
      <Tooltip title={FEEDBACK_ACTION_LABELS[value]}>
        <IconButton
          size="small"
          aria-label={FEEDBACK_ACTION_LABELS[value]}
          aria-pressed={active}
          onClick={() => handleRate(value)}
          sx={{ p: 0.25, color: active ? 'text.primary' : 'text.secondary' }}
        >
          {active ? <ActiveIcon fontSize="inherit" /> : <IdleIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box
      data-testid="message-feedback"
      sx={{
        mt: 0.5,
        opacity: rating ? 1 : QUIET_OPACITY,
        transition: 'opacity 120ms ease-in',
        '&:hover, &:focus-within': { opacity: 1 }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {ratingButton(FEEDBACK_RATINGS.UP, ThumbUpAltIcon, ThumbUpOffAltIcon)}
        {ratingButton(FEEDBACK_RATINGS.DOWN, ThumbDownAltIcon, ThumbDownOffAltIcon)}
        {rating && !commentOpen && (
          <Link component="button" type="button" variant="caption" underline="hover" onClick={openComment}>
            {commentLinkLabel(comment)}
          </Link>
        )}
      </Box>

      {rating && comment && !commentOpen && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {comment}
        </Typography>
      )}

      {commentOpen && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={3}
            autoFocus
            placeholder="What was wrong or right about this answer? (optional)"
            value={commentDraft}
            onChange={e => setCommentDraft(e.target.value)}
            inputProps={{ 'aria-label': 'Feedback comment', maxLength: FEEDBACK_COMMENT_MAX_LENGTH }}
          />
          <Button size="small" onClick={submitComment} sx={{ textTransform: 'none' }}>
            Submit
          </Button>
          <Button size="small" color="inherit" onClick={dismissComment} sx={{ textTransform: 'none' }}>
            Dismiss
          </Button>
        </Box>
      )}
    </Box>
  );
}
